import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tag } from '@/shared/components/ui/tag/tag';
import { TGroupNameProps } from '@/shared/features/groups/components/group-name/group-name.types';
import { GroupPath } from '@/shared/features/groups/components/group-path/group-path';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { GROUP_PATH_SEPARATOR } from '@/shared/features/groups/utils/group-path/elide-group-path';
import { useReadResourceGroupNameByIdQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';

export const GroupName: React.FC<TGroupNameProps> = ({
  groupId,
  variant = 'tag',
  disableNavigation = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { groupRoutePath } = useGroupsConfig();
  const isText = variant === 'text';
  const { data, error, isLoading, isFetching } =
    useReadResourceGroupNameByIdQuery(
      { groupId: groupId! },
      { skip: !groupId },
    );
  const isPermissionDenied =
    !isLoading && !isFetching && isPermissionDeniedError(error);
  const group = data?.readResourceGroup;
  const getFullPaths = (): string[] => {
    if (group?.fullPaths?.length) {
      return group.fullPaths;
    }
    return group?.name ? [group.name] : [];
  };
  const fullPaths = getFullPaths();

  if (!groupId) {
    return '-';
  }

  if (isPermissionDenied) {
    if (isText) {
      return (
        <Typography variant="inherit" component="span">
          {t('groups:restricted_group')}
        </Typography>
      );
    }
    return (
      <Stack direction="row" alignItems="center" gap="0.25rem">
        <Tag
          label={
            <Typography variant="p8">{t('groups:restricted_group')}</Typography>
          }
        />
      </Stack>
    );
  }

  if (isLoading) {
    return <Skeleton />;
  }

  if (fullPaths.length === 0) {
    return '-';
  }

  const fullPath = fullPaths.join(GROUP_PATH_SEPARATOR);

  if (isText) {
    return <GroupPath fullPath={fullPath} variant="text" />;
  }

  return (
    <GroupPath
      fullPath={fullPath}
      onClick={
        disableNavigation
          ? undefined
          : () => navigate(`${groupRoutePath}/${groupId}`)
      }
    />
  );
};
